// services/emailService.js — Send activity update emails via Microsoft Graph
import { mailSendRequest } from "../authConfig";

function buildEmailHtml({ activity, update, senderName, dashboardUrl }) {
  const priorityStyle = {
    High:   { bg: "#FDE7E9", color: "#D13438" },
    Medium: { bg: "#FFF4EC", color: "#E65C00" },
    Low:    { bg: "#E9F5E9", color: "#107C10" },
  }[activity.priority] || { bg: "#F0F0F0", color: "#666" };
  const statusStyle = {
    "In Progress":  { bg: "#EBF3FF", color: "#0066FF" },
    "Completed":    { bg: "#E9F5E9", color: "#107C10" },
    "On Hold":      { bg: "#FFF4EC", color: "#E65C00" },
    "Not Started":  { bg: "#F0F0F0", color: "#666" },
  }[activity.status] || { bg: "#F0F0F0", color: "#666" };

  const dueDate = activity.dueDate
    ? new Date(activity.dueDate).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    : "Not set";
  const updateDate = new Date(update.date).toLocaleDateString("en-GB", {
    day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
  });

  const daysLeft = activity.dueDate
    ? Math.ceil((new Date(activity.dueDate) - new Date()) / (1000 * 60 * 60 * 24))
    : null;
  const daysLabel = daysLeft === null ? "" : daysLeft < 0 ? `${Math.abs(daysLeft)} days overdue` : daysLeft === 0 ? "Due today" : `${daysLeft} days remaining`;
  const daysColor = daysLeft !== null && daysLeft < 0 ? "#D13438" : daysLeft !== null && daysLeft <= 3 ? "#E65C00" : "#107C10";

  return `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head>
<body style="margin:0;padding:0;background:#F0F2F5;font-family:Segoe UI,Arial,sans-serif;">
  <div style="max-width:620px;margin:24px auto;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.08);">

    <div style="background:linear-gradient(135deg,#001A3D 0%,#002D7A 100%);padding:28px 32px;display:flex;align-items:center;gap:16px;">
      <div style="width:48px;height:48px;border-radius:10px;background:linear-gradient(135deg,#0066FF,#00C4CC);display:inline-flex;align-items:center;justify-content:center;font-weight:800;font-size:17px;color:#fff;">LO</div>
      <div style="display:inline-block;vertical-align:middle;margin-left:16px;">
        <div style="color:#fff;font-weight:700;font-size:19px;line-height:1.2;">LekOil SCM Dashboard</div>
        <div style="color:rgba(255,255,255,0.65);font-size:13px;margin-top:2px;">Activity Update Notification</div>
      </div>
    </div>

    <div style="background:#0066FF;padding:10px 32px;">
      <span style="color:#fff;font-size:12px;font-weight:700;letter-spacing:0.8px;">TASK UPDATE NOTIFICATION</span>
    </div>

    <div style="padding:32px;">
      <p style="margin:0 0 6px;font-size:12px;color:#888;font-weight:600;letter-spacing:0.5px;">ACTIVITY TITLE</p>
      <h1 style="margin:0 0 16px;font-size:22px;color:#001A3D;font-weight:700;line-height:1.3;">${activity.title}</h1>

      <div style="margin-bottom:24px;">
        <span style="display:inline-block;background:#E3F0FF;color:#0055CC;padding:4px 12px;border-radius:20px;font-size:12px;font-weight:600;margin-right:6px;">${activity.category}</span>
        <span style="display:inline-block;background:${priorityStyle.bg};color:${priorityStyle.color};padding:4px 12px;border-radius:20px;font-size:12px;font-weight:600;margin-right:6px;">${activity.priority} Priority</span>
        <span style="display:inline-block;background:${statusStyle.bg};color:${statusStyle.color};padding:4px 12px;border-radius:20px;font-size:12px;font-weight:600;">${update.status || activity.status}</span>
      </div>

      <div style="background:#F5F8FF;border-left:4px solid #0066FF;padding:18px 20px;border-radius:0 10px 10px 0;margin-bottom:28px;">
        <p style="margin:0 0 6px;font-size:11px;color:#0066FF;font-weight:700;letter-spacing:0.5px;">KEY ACTIVITIES</p>
        <p style="margin:0 0 10px;font-size:15px;color:#1A1A2E;line-height:1.6;">${update.note || "(No note provided)"}</p>
        <p style="margin:0;font-size:12px;color:#888;">Updated by <strong>${senderName || update.byName}</strong> &nbsp;·&nbsp; ${updateDate}</p>
      </div>

      ${update.lookAhead ? `
      <div style="background:#F0F4FF;border-left:4px solid #C8D6FF;padding:18px 20px;border-radius:0 10px 10px 0;margin-bottom:28px;">
        <p style="margin:0 0 6px;font-size:11px;color:#0044BB;font-weight:700;letter-spacing:0.5px;">LOOK AHEAD</p>
        <p style="margin:0;font-size:15px;color:#1A1A2E;line-height:1.6;">${update.lookAhead}</p>
      </div>` : ""}

      <table style="width:100%;border-collapse:separate;border-spacing:12px;margin:-12px -12px 24px;table-layout:fixed;">
        <tr>
          <td style="background:#F8F9FC;padding:16px;border-radius:10px;vertical-align:top;width:50%;">
            <p style="margin:0 0 4px;font-size:11px;color:#888;font-weight:700;letter-spacing:0.5px;">DUE DATE</p>
            <p style="margin:0;font-size:15px;color:#001A3D;font-weight:700;">${dueDate}</p>
            ${daysLabel ? `<p style="margin:4px 0 0;font-size:12px;color:${daysColor};font-weight:600;">${daysLabel}</p>` : ""}
          </td>
          <td style="background:#F8F9FC;padding:16px;border-radius:10px;vertical-align:top;width:50%;">
            <p style="margin:0 0 4px;font-size:11px;color:#888;font-weight:700;letter-spacing:0.5px;">ASSIGNED TO</p>
            <p style="margin:0;font-size:15px;color:#001A3D;font-weight:700;">${activity.assignedToName || "—"}</p>
            <p style="margin:4px 0 0;font-size:12px;color:#888;">${activity.assignedTo || ""}</p>
          </td>
        </tr>
      </table>

      ${activity.description ? `
      <div style="margin-bottom:28px;">
        <p style="margin:0 0 8px;font-size:12px;color:#888;font-weight:700;letter-spacing:0.5px;">TASK DESCRIPTION</p>
        <p style="margin:0;font-size:14px;color:#444;line-height:1.7;">${activity.description}</p>
      </div>` : ""}

      ${dashboardUrl ? `
      <a href="${dashboardUrl}" style="display:inline-block;background:linear-gradient(135deg,#0066FF,#002D7A);color:#fff;padding:14px 32px;border-radius:10px;text-decoration:none;font-weight:700;font-size:14px;letter-spacing:0.3px;">View in SCM Dashboard &rarr;</a>` : ""}
    </div>

    <div style="background:#F8F9FC;padding:20px 32px;border-top:1px solid #E8EAED;">
      <p style="margin:0;font-size:12px;color:#999;line-height:1.6;">You received this notification because you are listed as the owner of this task in the LekOil SCM Activity Tracker.</p>
      <p style="margin:8px 0 0;font-size:11px;color:#BBB;">&copy; ${new Date().getFullYear()} LekOil Limited &mdash; Supply Chain Management</p>
    </div>
  </div>
</body>
</html>`;
}

function buildMailtoFallback({ activity, update, senderName }) {
  const subject = encodeURIComponent(`[LekOil SCM] Task Update: ${activity.title}`);
  const dueDate = activity.dueDate
    ? new Date(activity.dueDate).toLocaleDateString("en-GB")
    : "Not set";
  const updateDate = new Date(update.date).toLocaleDateString("en-GB", {
    day: "numeric", month: "long", year: "numeric",
  });
  const body = encodeURIComponent(
    `Dear ${activity.ownerName || "Task Owner"},\n\n` +
    `This is an update for the following SCM activity:\n\n` +
    `Task: ${activity.title}\n` +
    `Category: ${activity.category}  |  Priority: ${activity.priority}  |  Status: ${update.status || activity.status}\n` +
    `Due Date: ${dueDate}\n\n` +
    `─── KEY ACTIVITIES (${updateDate}) ───\n` +
    `${update.note || "(No note provided)"}\n\n` +
    (update.lookAhead ? `─── LOOK AHEAD ───\n${update.lookAhead}\n\n` : "") +
    `Updated by: ${senderName || update.byName}\n\n` +
    `─────────────────────────────────────\n` +
    `LekOil Limited — Supply Chain Management`,
  );
  const toEmails = activity.owners?.length > 0
    ? activity.owners.map(o => o.email).join(",")
    : (activity.ownerEmail || "");
  return `mailto:${toEmails}?subject=${subject}&body=${body}`;
}

export async function sendActivityUpdateEmail(instance, accounts, { activity, update, senderName }) {
  const dashboardUrl = window.location.origin;

  try {
    const tokenRes = await instance.acquireTokenSilent({
      ...mailSendRequest,
      account: accounts[0],
    }).catch(() => instance.acquireTokenPopup(mailSendRequest));

    const token = tokenRes.accessToken;
    const htmlContent = buildEmailHtml({ activity, update, senderName, dashboardUrl });

    const res = await fetch("https://graph.microsoft.com/v1.0/me/sendMail", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        message: {
          subject: `[LekOil SCM] Task Update: ${activity.title}`,
          body: { contentType: "HTML", content: htmlContent },
          toRecipients: activity.owners?.length > 0
            ? activity.owners.map(o => ({ emailAddress: { address: o.email, name: o.name || o.email } }))
            : [{ emailAddress: { address: activity.ownerEmail, name: activity.ownerName || activity.ownerEmail } }],
        },
        saveToSentItems: true,
      }),
    });

    if (res.ok || res.status === 202) {
      return { success: true, method: "graph" };
    }
    throw new Error(`Graph API returned ${res.status}`);
  } catch {
    const mailtoUrl = buildMailtoFallback({ activity, update, senderName });
    window.open(mailtoUrl, "_blank");
    return { success: true, method: "mailto" };
  }
}
